import {Button, openDeleteDialog, Separator} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {concat, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {AuditParamDefinition} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {deleteAuditAssets} from '../../../store/carAudit/actions';
import {deleteAssets} from '../../../store/carAudit/reducer';
import {selectAudit, selectPhotoDocumentation} from '../../../store/carAudit/selectors';
import {AuditImageSelectionContext} from '../contexts/AuditImageSelectionContext';
import {useAuditImageSelection} from '../hooks/useAuditImageSelection';
import {useConditionContext} from '../hooks/useConditionContext';
import {AuditCategoryUniqueKey} from '../types/AuditCategoryUniqueKey';
import {AuditParamType} from '../types/AuditParamType';
import {findByParamType} from '../utils/findByParamType';
import {findCategoryByUniqueKey} from '../utils/findCategoryByUniqueKey';
import {
  getPhotoDocumentationImageFields,
  PhotoDocumentationField,
} from '../utils/getPhotoDocumentationImageFields';
import {PhotoDocumentationCard} from './PhotoDocumentationCard';

export function PhotoDocumentation(props: TestIdProps) {
  const dispatch = useThunkDispatch();
  const photoDocumentation = useSelector(selectPhotoDocumentation);
  const audit = useSelector(selectAudit);
  const {isCreateVehicle, isDisabledForUser, handleChangeCategory} = useConditionContext();

  const [isSelectionMode, enterSelectionMode, exitSelectionMode] = useBoolean();
  const selection = useAuditImageSelection();

  if (!photoDocumentation) {
    return null;
  }

  const morePhotos = findCategoryByUniqueKey(
    photoDocumentation,
    AuditCategoryUniqueKey.MORE_PHOTOS
  );
  const morePhotosParam = findByParamType(
    morePhotos?.paramDefinitions?.relatedActions,
    AuditParamType.MORE_PHOTOS
  );

  const videos = findCategoryByUniqueKey(photoDocumentation, AuditCategoryUniqueKey.VIDEOS);
  const videosParam = findByParamType(
    videos?.paramDefinitions?.relatedActions,
    AuditParamType.VIDEO
  );

  const paramDefinitions = photoDocumentation.childCategories?.reduce(
    (relatedActions: AuditParamDefinition[], category) => {
      if (isNotNil(category.paramDefinitions?.relatedActions) && category.uniqueKey !== 'VIDEOS') {
        const actions = category.paramDefinitions.relatedActions.filter(
          (relatedAction) => relatedAction.type !== AuditParamType.MORE_PHOTOS
        );
        return isNotNilOrEmpty(actions) ? concat(relatedActions, actions) : relatedActions;
      }
      return relatedActions;
    },
    []
  );

  const hasParamDefinitions = isNotNilOrEmpty(paramDefinitions);
  const hasMorePhotos = isNotNilOrEmpty(morePhotosParam) && isNotNilOrEmpty(morePhotos);
  const hasVideos = isNotNilOrEmpty(videosParam) && isNotNilOrEmpty(videos);

  const photoFields: PhotoDocumentationField[] = [
    ...(paramDefinitions ?? []).map((paramDefinition) => ({
      categoryId: photoDocumentation.id,
      paramDefinitionId: paramDefinition.id,
    })),
    ...(hasMorePhotos
      ? [{categoryId: morePhotos!.id, paramDefinitionId: morePhotosParam!.id}]
      : []),
  ];

  const imageFields = getPhotoDocumentationImageFields(audit?.assets ?? {}, photoFields);
  const areBulkActionsAvailable = !isDisabledForUser && selection.hasSelectableImages;

  const handleToggleSelectionMode = () => {
    if (isSelectionMode) {
      selection.clearImageSelection();
      exitSelectionMode();
    } else {
      enterSelectionMode();
    }
  };

  const handleBulkDelete = () => {
    const selectedImageIds = selection.getSelectedImages();
    if (isNilOrEmpty(selectedImageIds)) {
      return;
    }

    openDeleteDialog({
      text: i18n.t('entity.photo.actions.deleteSelectedPhotosConfirmation', {
        count: selectedImageIds.length,
      }),
      onConfirm: () =>
        dispatch(deleteAuditAssets.action({requestBody: {imageIds: selectedImageIds}}))
          .unwrap()
          .then(() => {
            const selectedImageIdSet = new Set(selectedImageIds);
            imageFields.forEach((field) => {
              const imageIdsToDelete = field.imageIds.filter((imageId) =>
                selectedImageIdSet.has(imageId)
              );
              if (isNotNilOrEmpty(imageIdsToDelete)) {
                dispatch(
                  deleteAssets({
                    categoryId: field.categoryId,
                    paramDefinitionId: field.paramDefinitionId,
                    imageIds: imageIdsToDelete,
                  })
                );
              }
            });

            selection.clearImageSelection();
            exitSelectionMode();
            return handleChangeCategory();
          })
          .catch(handleApiError),
    });
  };

  return (
    <AuditImageSelectionContext.Provider
      value={{
        isBulkActionsEnabled: true,
        isSelectionMode,
        isImageSelected: selection.isImageSelected,
        toggleImageSelection: selection.toggleImageSelection,
        registerSelectableImage: selection.registerSelectableImage,
        unregisterSelectableImage: selection.unregisterSelectableImage,
      }}
    >
      <VStack spacing={4}>
        <Show when={areBulkActionsAvailable}>
          <HStack spacing={2} justify="flex-end" align="center">
            <Show when={isSelectionMode}>
              <Button
                variant="ghostLink"
                leftIcon="action/done_all"
                title={i18n.t('general.actions.selectAll')}
                onClick={selection.selectAllImages}
                data-testid={suffixTestId('photoDocumentation-selectAll', props)}
              />
              <Show when={selection.selectedCount > 0}>
                <Button
                  variant="dangerGhost"
                  leftIcon="action/delete"
                  title={i18n.t('entity.photo.actions.deleteSelectedPhotos')}
                  suffix={`(${selection.selectedCount})`}
                  onClick={handleBulkDelete}
                  data-testid={suffixTestId('photoDocumentation-deleteSelected', props)}
                />
              </Show>
            </Show>
            <Button
              variant="ghostLink"
              leftIcon="image/filter_none"
              title={
                isSelectionMode
                  ? i18n.t('general.actions.cancel')
                  : i18n.t('general.actions.bulkActions')
              }
              onClick={handleToggleSelectionMode}
              data-testid={suffixTestId('photoDocumentation-toggleSelectionMode', props)}
            />
          </HStack>
        </Show>

        <Grid columns={8} align="flex-end">
          {paramDefinitions?.map((paramDefinition, index) => (
            <PhotoDocumentationCard
              key={`${paramDefinition.id}-${paramDefinition.label}`}
              categoryId={photoDocumentation.id}
              paramDefinition={paramDefinition}
              label={paramDefinition.label}
              data-testid={suffixTestId(`photoDocumentationCard-[${index}]`, props)}
            />
          ))}
        </Grid>
        <Show when={hasParamDefinitions && hasMorePhotos}>
          <Separator />
        </Show>
        <Show when={hasMorePhotos}>
          <PhotoDocumentationCard
            categoryId={morePhotos?.id!} // eslint-disable-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
            paramDefinition={morePhotosParam!}
            label={morePhotosParam?.label}
            multiple
            data-testid={suffixTestId('morePhotoDocumentationCard', props)}
          />
        </Show>
        {/*
          AuditVideos component is not ready to be used in VehicleCreateForm (isCreateVehicle)

          To use it while vehicle is being created, instead of calling onVideoProcessed
          call pushConditionAsset from VehicleCreate context and implement assigning the video
          to the audit in VehicleCreateForm's saveConditionData (there the videos are taken
          from the context by getConditionAssets)
         */}
        <Show when={hasVideos && !isCreateVehicle}>
          <Show when={hasParamDefinitions || hasMorePhotos}>
            <Separator />
          </Show>
          <PhotoDocumentationCard
            categoryId={videos?.id!} // eslint-disable-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
            paramDefinition={videosParam!}
            label={videosParam?.label}
            multiple
            isVideo
            data-testid={suffixTestId('videosDocumentationCard', props)}
          />
        </Show>
      </VStack>
    </AuditImageSelectionContext.Provider>
  );
}
